import { getCludoEngineId, getConfig, LanguageService, RocheEndpoint, StringService } from '@repo/roche-xx-shared';
import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date';
import { RocheUIStoryType } from '@rocheglobal/roche-ui';

/**
 * Interface for Cludo API response document fields
 */
interface CludoDocumentFields {
  [key: string]: {
    Value: string;
  };
}

/**
 * Interface for Cludo API response document
 */
interface CludoDocument {
  Fields?: CludoDocumentFields;
}

/**
 * Interface for Cludo API response
 */
interface CludoResponse {
  TotalDocument: number;
  TypedDocuments: CludoDocument[];
}

/**
 * Interface for Cludo search filters
 */
interface CludoFilters {
  [key: string]: string[];
}

/**
 * Interface for Cludo search sort options
 */
interface CludoSort {
  [key: string]: 'asc' | 'desc';
}

/**
 * Interface for base Cludo search parameters
 */
interface CludoSearchParams {
  query: string;
  page: number;
  perPage?: number;
  filters?: CludoFilters;
  sort?: CludoSort;
  enableFacetFiltering?: boolean;
}

/**
 * Configuration for fuzzyness behavior
 * - 'ON': Always use fuzzyness
 * - 'OFF': Never use fuzzyness
 * - 'AUTO': Use fuzzyness only for special characters
 */
type FuzzinessMode = 'ON' | 'OFF' | 'AUTO';

/**
 * Determines whether to use fuzzy search based on configuration and search query
 *
 * @param searchQuery - The search query string
 * @param mode - The fuzzyness mode configuration
 * @returns Whether fuzzy search should be enabled
 */
const determineFuzziness = (searchQuery: string, mode: FuzzinessMode = 'AUTO'): boolean => {
  if (mode === 'ON') {
    return true;
  }

  if (mode === 'OFF') {
    return false;
  }

  // AUTO mode: Use fuzzyness only if there are special characters in the searchQuery term
  const hasSpecialCharacters = /[^\x00-\x7F]/.test(searchQuery);
  return hasSpecialCharacters;
};

/**
 * Normalizes the fuzzyness configuration from environment variable
 *
 * @param cludoFuzziness - The fuzzyness configuration from environment
 * @returns Normalized fuzzyness mode
 */
const normalizeFuzzinessMode = (cludoFuzziness?: string): FuzzinessMode => {
  if (cludoFuzziness?.toUpperCase() === 'ON') {
    return 'ON';
  }

  if (cludoFuzziness?.toUpperCase() === 'OFF') {
    return 'OFF';
  }

  return 'AUTO';
};

/**
 * Fetches a search request to the Cludo API
 *
 * @param params - The search parameters
 * @returns The Cludo API response
 */
export const cludoSearch = async (params: CludoSearchParams): Promise<CludoResponse> => {
  const locale = LanguageService.getActiveLanguage();

  const { GATSBY_WEBSITE_NAME, GATSBY_CLUDO_FUZZINESS, GATSBY_CLUDO_CUSTOMER_ID } = getConfig();

  const cludoEngineId = getCludoEngineId(locale);
  const fuzzinessMode = normalizeFuzzinessMode(GATSBY_CLUDO_FUZZINESS);
  const useFuzziness = determineFuzziness(params.query, fuzzinessMode);

  const response = await fetch(`https://api.cludo.com/api/v3/${GATSBY_CLUDO_CUSTOMER_ID}/${cludoEngineId}/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `SiteKey ${btoa(`${GATSBY_CLUDO_CUSTOMER_ID}:${cludoEngineId}:SiteKey`)}`,
    },
    body: JSON.stringify({
      query: useFuzziness ? `${params.query}~` : `${params.query}`,
      perPage: params.perPage || 10,
      page: params.page,
      enableFacetFiltering: params.enableFacetFiltering ?? true,
      filters: {
        WebsiteName: [GATSBY_WEBSITE_NAME],
        Lang: [locale],
        ...params.filters,
      },
      ...(params.sort && { sort: params.sort }),
    }),
  });

  return response.json();
};

/**
 * Main search result item
 */
export interface CludoSearchResultItem {
  title: string;
  formattedReleaseDate: string;
  publishedDate: string;
  url: string;
  teaserType?: RocheUIStoryType;
  variant: string;
  description: string;
  image: string;
}

/**
 * Parameters for main search
 */
interface FetchMainSearchParams {
  searchQuery: string;
  page: number;
  categoryFilter: string;
  pageSize?: number;
}

/**
 * Fetches and transforms main search results using Cludo API
 *
 * @param params - The search parameters including query, page, category filter, and page size
 * @returns Transformed search results
 */
export const fetchMainSearchResults = async ({
  searchQuery,
  categoryFilter,
  page,
  pageSize = 6,
}: FetchMainSearchParams): Promise<RocheEndpoint<CludoSearchResultItem[]>> => {
  const cludoResponse = await cludoSearch({
    query: searchQuery,
    page,
    perPage: pageSize,
    filters: {
      Category: [categoryFilter],
    },
  });

  const transformedResults: RocheEndpoint<CludoSearchResultItem[]> = {
    Total: cludoResponse.TotalDocument,
    Items: [],
    Page: page,
  };

  for await (const [index, item] of cludoResponse.TypedDocuments.entries()) {
    const image = item.Fields?.['Teaser Image']?.Value || item.Fields?.Image?.Value;
    const storyType =
      item.Fields?.['Story Type']?.Value === 'none'
        ? RocheUIStoryType.DEFAULT
        : (item.Fields?.['Story Type']?.Value as RocheUIStoryType);
    const publishedDate =
      item.Fields?.['Published Date']?.Value && categoryFilter !== 'pages'
        ? new Date(item.Fields?.['Published Date']?.Value).toISOString()
        : '';
    const title =
      item.Fields?.['Teaser Title']?.Value ||
      item.Fields?.Title?.Value ||
      item.Fields?.fc_Title?.Value ||
      item.Fields?.fc_ResponseFileName?.Value ||
      StringService.capitalize(categoryFilter);

    transformedResults.Items[index] = {
      title,
      formattedReleaseDate: await DateService.formatDate(publishedDate as string | Date, 'dateFull'),
      publishedDate,
      url: item.Fields?.Url?.Value,
      teaserType: storyType,
      variant: categoryFilter,
      image,
      description:
        categoryFilter === 'documents'
          ? StringService.getExtension(item.Fields?.Url?.Value).toUpperCase()
          : item.Fields?.['Teaser Description']?.Value || item.Fields?.Description?.Value,
    };
  }

  return transformedResults;
};

/**
 * Interface for data search result item (news/media)
 */
export interface CludoDataSearchResultItem {
  localisedTitle: string;
  link: string;
  ReleaseDateTime: string;
}

/**
 * Parameters for fetching data search results
 */
interface FetchDataSearchParams {
  query: string;
  page: number;
  type?: string;
  perPage?: number;
}

/**
 * Fetches and transforms data search results using Cludo API
 * Used by the data search feature (news, media releases, investor updates)
 *
 * @param params - The search parameters including query, page, type, and page size
 * @returns Transformed search results in RocheEndpoint format
 */
export const fetchDataSearchResults = async ({
  query,
  page,
  type = 'media',
  perPage = 10,
}: FetchDataSearchParams): Promise<RocheEndpoint<Partial<CludoDataSearchResultItem>[]>> => {
  const cludoResponse = await cludoSearch({
    query,
    page,
    perPage,
    filters: {
      Type: [type],
    },
    sort: {
      Date: 'desc',
    },
  });

  const transformedResults: RocheEndpoint<Partial<CludoDataSearchResultItem>[]> = {
    Total: cludoResponse.TotalDocument,
    Items: [],
    Page: page,
  };

  for (const [index, item] of cludoResponse.TypedDocuments.entries()) {
    transformedResults.Items[index] = {
      localisedTitle: item.Fields?.Title?.Value,
      link: item.Fields?.Url?.Value,
      ReleaseDateTime: item.Fields?.Date?.Value,
    };
  }

  return transformedResults;
};
